import { useState, useMemo, useCallback } from 'react';
import moment from 'moment';
import useApiRequest from 'hooks/useApiRequest';
import useAsync from 'hooks/useAsync';

import { dateFormatForAPI } from 'utils/date';
import {
  formatRecurrencePayload,
  formatBookingItems,
} from '../../services/formatBasketPayload';

interface Slot {
  startTime: string;
  endTime: string;
}

interface Space {
  id: number;
  priceIncVat: number;
}

interface BookingItem {
  startTime: string;
  endTime: string;
  space: { id: number };
}

const getSpacePrice = (spaces, spaceId) => {
  const { priceIncVat = 0 } = spaces.find(({ id }) => id === spaceId) || {};
  return Number(priceIncVat);
};

const fixAvailability = (availability, spaces) =>
  availability
    .map(({ startTime, endTime, entries = [] }) => ({
      [`${startTime}-${endTime}`]: entries.flatMap(entry =>
        entry.availability.map(({ spaceId, state, durationInHours = 0 }) => ({
          spaceId,
          date: entry.date,
          priceWithVat: getSpacePrice(spaces, spaceId) * durationInHours,
          startTime,
          endTime,
          availabilityFlag: state,
        }))
      ),
    }))
    .flat()
    .reduce((prev, curr) => ({ ...prev, ...curr }), {});

const formatTime = time => moment(time).format('HH:mm');

const fixSlot = ({ startTime, endTime }) => ({ startTime, endTime });
const fixBookingItem = ({
  startTime,
  endTime,
  space: { id: spaceId } = {},
}) => ({
  date: moment(startTime).format(dateFormatForAPI),
  startTime: formatTime(startTime),
  endTime: formatTime(endTime),
  spaceId,
});

const emptyObject = {};
const emptyList = [];

const useAvailability = (
  date: Date,
  clientServiceId: string,
  spaces: Space[],
  slots: Slot[],
  recurrence: object = emptyObject,
  bookingItems: object | BookingItem[] = emptyObject,
  bookingId: number | number[] = emptyList
) => {
  const [availability, setAvailability] = useState({});

  const apiRequest = useApiRequest();

  const requestValid =
    date &&
    clientServiceId &&
    spaces.length > 0 &&
    slots.length > 0 &&
    slots[0].startTime &&
    slots[0]?.endTime;

  const usingRecurrence = bookingItems === emptyObject;

  const paramsJson = useMemo(
    () =>
      JSON.stringify({
        date: moment(date).format(dateFormatForAPI),
        clientServiceId,
        spaceIds: spaces.map(({ id }) => id),
        slots: slots.map(fixSlot),
        ...(!usingRecurrence || ('checked' in recurrence && !recurrence.checked)
          ? {}
          : {
              recurrence: formatRecurrencePayload(recurrence),
            }),
        ...(usingRecurrence
          ? {}
          : {
              bookingItems:
                bookingItems instanceof Array
                  ? bookingItems.map(fixBookingItem)
                  : formatBookingItems(bookingItems),
            }),
        ...(bookingId instanceof Array && bookingId.length === 0
          ? {}
          : {
              excludedBookingIds:
                bookingId instanceof Array ? bookingId : [bookingId],
            }),
      }),
    [
      date,
      clientServiceId,
      usingRecurrence,
      recurrence,
      bookingItems,
      spaces,
      slots,
      bookingId,
    ]
  );

  const spacesJson = useMemo(() => JSON.stringify(spaces), [spaces]);

  const clearData = useCallback(async () => {
    setAvailability({});
  }, []);

  const fetchData = useCallback(async () => {
    const {
      data: { slots = [] },
    } = await apiRequest.post(
      `/v1/spaces/availability`,
      JSON.parse(paramsJson)
    );

    setAvailability(fixAvailability(slots, JSON.parse(spacesJson)));
  }, [apiRequest, paramsJson, spacesJson]);

  const { loading, error, refresh } = useAsync(
    requestValid ? fetchData : clearData,
    requestValid
  );

  return { data: availability, loading, error, refresh };
};

export default useAvailability;
